<template>
    <section class="relative min-h-screen w-full bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div class="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <!-- Header Section -->
            <div class="mb-8 text-center lg:text-left">
                <div class="inline-flex items-center justify-center lg:justify-start mb-4">
                    <div class="bg-blue-100 p-3 rounded-full mr-4">
                        <svg class="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M15 17h5l-5 5v-5zM9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                    </div>
                    <div>
                        <h1 class="text-4xl font-bold text-gray-900">Notifications</h1>
                        <p class="mt-2 text-lg text-gray-600">Stay updated with your latest activities and interactions
                        </p>
                    </div>
                </div>
            </div>

            <!-- Stats Cards -->
            <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
                    <div class="flex items-center">
                        <div class="bg-blue-100 p-3 rounded-full">
                            <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                        </div>
                        <div class="ml-4">
                            <p class="text-sm font-medium text-gray-600">Total Notifications</p>
                            <p class="text-2xl font-bold text-gray-900">{{ pagination.total }}</p>
                        </div>
                    </div>
                </div>

                <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
                    <div class="flex items-center">
                        <div class="bg-yellow-100 p-3 rounded-full">
                            <svg class="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <div class="ml-4">
                            <p class="text-sm font-medium text-gray-600">Unread</p>
                            <p class="text-2xl font-bold text-yellow-600">{{ unreadCount }}</p>
                        </div>
                    </div>
                </div>

                <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
                    <div class="flex items-center">
                        <div class="bg-green-100 p-3 rounded-full">
                            <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <div class="ml-4">
                            <p class="text-sm font-medium text-gray-600">Read</p>
                            <p class="text-2xl font-bold text-green-600">{{ pagination.total - unreadCount }}</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Filters Section -->
            <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
                <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div class="flex flex-col sm:flex-row gap-4">
                        <div class="flex items-center space-x-3">
                            <label class="text-sm font-semibold text-gray-700">Filter by type:</label>
                            <select v-model="filterType" @change="refetchNotifications"
                                class="border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white">
                                <option value="">All types</option>
                                <option value="liked">Likes</option>
                                <option value="commented">Comments</option>
                                <option value="replied">Replies</option>
                                <option value="thinktank_like">Think Tank Likes</option>
                                <option value="thinktank_comment">Think Tank Comments</option>
                                <option value="thinktank_reply">Think Tank Replies</option>
                                <option value="submitted">Survey Responses</option>
                            </select>
                        </div>

                        <div class="flex items-center space-x-3">
                            <label class="text-sm font-semibold text-gray-700">Status:</label>
                            <select v-model="filterRead" @change="refetchNotifications"
                                class="border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white">
                                <option value="">All</option>
                                <option value="false">Unread</option>
                                <option value="true">Read</option>
                            </select>
                        </div>
                    </div>

                    <div class="flex items-center space-x-3">
                        <button v-if="unreadCount > 0" @click="markAllAsRead"
                            class="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-2.5 rounded-lg text-sm font-semibold hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 transform hover:scale-105 shadow-md">
                            <svg class="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M5 13l4 4L19 7" />
                            </svg>
                            Mark all as read
                        </button>

                        <button @click="refetchNotifications"
                            class="bg-gray-100 text-gray-700 px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200">
                            <svg class="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            Refresh
                        </button>
                    </div>
                </div>
            </div>

            <!-- Notifications List -->
            <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <!-- Loading State -->
                <div v-if="$apollo.queries.notifications.loading" class="p-12 text-center">
                    <base-spinner class="w-8 h-8 text-blue-600"></base-spinner>
                    <p class="mt-4 text-lg font-medium text-gray-900">Loading notifications...</p>
                    <p class="text-gray-500">Please wait while we fetch your latest updates</p>
                </div>

                <!-- Empty State -->
                <div v-else-if="notifications.length === 0" class="p-12 text-center">
                    <div class="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-6">
                        <svg class="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <h3 class="text-xl font-semibold text-gray-900 mb-2">All caught up!</h3>
                    <p class="text-gray-500 max-w-md mx-auto">You don't have any notifications right now. Check back
                        later for updates on your activities and interactions.</p>
                </div>

                <!-- Notifications -->
                <div v-else>
                    <div v-for="notification in notifications" :key="notification.id"
                        class="p-6 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors duration-200"
                        :class="{ 'bg-blue-50 border-l-4 border-l-blue-500': !notification.read_at }">
                        <div class="flex items-start space-x-4">
                            <!-- Profile Picture with Notification Type Emoji -->
                            <div class="relative flex-shrink-0">
                                <img :src="getProfilePicture(notification.actor)" :alt="getUserName(notification.actor)"
                                    class="h-14 w-14 rounded-full object-cover border-3 border-gray-200 shadow-sm"
                                    @error="handleImageError">
                                <!-- Notification Type Emoji Badge -->
                                <div
                                    class="absolute -bottom-1 -right-1 h-6 w-6 rounded-full bg-white border-2 border-gray-200 flex items-center justify-center shadow-md">
                                    <span class="text-sm">{{ getNotificationEmoji(notification.action_type) }}</span>
                                </div>
                            </div>

                            <!-- Content -->
                            <div class="flex-1 min-w-0">
                                <div class="flex items-start justify-between mb-2">
                                    <div class="flex-1">
                                        <h3 class="text-lg font-semibold text-gray-900 mb-1">
                                            {{ notification.title || getFormattedNotificationText(notification) }}
                                        </h3>
                                        <p class="text-gray-600 leading-relaxed">
                                            {{ notification.message }}
                                        </p>
                                    </div>

                                    <div class="flex items-center space-x-2 ml-4">
                                        <span class="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                                            {{ formatTime(notification.created_at) }}
                                        </span>
                                        <button @click="deleteNotification(notification.id)"
                                            class="text-gray-400 hover:text-red-500 p-1 rounded-full hover:bg-red-50 transition-colors duration-200">
                                            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                <path fill-rule="evenodd"
                                                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                                    clip-rule="evenodd" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>

                                <!-- Additional data -->
                                <div v-if="parsedNotificationData(notification.data)" class="mt-4">
                                    <div v-if="parsedNotificationData(notification.data).comment_excerpt"
                                        class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-3">
                                        <p class="text-sm text-blue-800">
                                            <span class="font-semibold">💬 Comment:</span> "{{
                                                parsedNotificationData(notification.data).comment_excerpt }}"
                                        </p>
                                    </div>

                                    <div v-if="parsedNotificationData(notification.data).reply_excerpt"
                                        class="bg-green-50 border border-green-200 rounded-lg p-4 mb-3">
                                        <p class="text-sm text-green-800">
                                            <span class="font-semibold">↩️ Reply:</span> "{{
                                                parsedNotificationData(notification.data).reply_excerpt }}"
                                        </p>
                                    </div>

                                    <div v-if="parsedNotificationData(notification.data).content"
                                        class="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-3">
                                        <p class="text-sm text-gray-800">
                                            <span class="font-semibold">📝 Content:</span> "{{
                                                parsedNotificationData(notification.data).content }}"
                                        </p>
                                    </div>

                                    <div v-if="parsedNotificationData(notification.data).comment_preview"
                                        class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-3">
                                        <p class="text-sm text-blue-800">
                                            <span class="font-semibold">💬 Comment:</span> "{{
                                                parsedNotificationData(notification.data).comment_preview }}"
                                        </p>
                                    </div>
                                </div>

                                <!-- Actions -->
                                <div class="mt-5 flex items-center space-x-4">
                                    <button v-if="!notification.read_at" @click="markAsRead(notification.id)"
                                        class="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200">
                                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M5 13l4 4L19 7" />
                                        </svg>
                                        Mark as read
                                    </button>

                                    <button v-else @click="markAsUnread(notification.id)"
                                        class="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200">
                                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                        Mark as unread
                                    </button>

                                    <button
                                        v-if="getNotificationViewLink(notification)"
                                        @click="navigateToNotification(notification)"
                                        class="inline-flex items-center px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg text-sm font-medium hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all duration-200">
                                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                        </svg>
                                        {{ getNotificationViewLink(notification).label }}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Pagination -->
            <div v-if="pagination.lastPage > 1" class="mt-8 flex justify-center">
                <div class="w-full bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                    <pagination :paginatorInfo="pagination" :setPage="changePage"></pagination>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
// Constants
const DEFAULT_AVATAR = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiNlNWU3ZWYiLz4KPHBhdGggZD0iTTIwIDEwQzIyLjA3NjEgMTAgMjQgMTIuMDIzOSAyNCAxNEMyNCAxNS45NzYxIDIyLjA3NjEgMTggMjAgMThDMTcuOTIzOSAxOCAxNiAxNS45NzYxIDE2IDE0QzE2IDEyLjAyMzkgMTcuOTIzOSAxMCAyMCAxMFoiIGZpbGw9IiM5Y2EzYWYiLz4KPHBhdGggZD0iTTI4IDI4QzI4IDI0LjY4NjMgMjQuNDE4MyAyMiAyMCAyMkMxNS41ODE3IDIyIDEyIDI0LjY4NjMgMTIgMjhIMjhaIiBmaWxsPSIjOWNhM2FmIi8+Cjwvc3ZnPgo=';

const TIME_INTERVALS = {
    MINUTE: 60000,
    HOUR: 3600000,
    DAY: 86400000,
    MONTH: 2592000000
};

const NOTIFICATION_ACTIONS = {
    'liked': 'liked your post',
    'commented': 'commented on your post',
    'replied': 'replied to your comment',
    'sent': 'sent you a notification',
    'thinktank_like': 'liked your think tank',
    'thinktank_comment': 'commented on your think tank',
    'thinktank_reply': 'replied to your think tank comment',
    'survey_response': 'submitted a response to your survey',
    'job_application': 'applied to your job',
    'event_registration': 'registered for your event',
    'profile_update': 'updated profile',
    'task_board_created': 'added you to the task board',
    'task_board_member_added': 'added you to the task board',
    'task_assigned': 'assigned you a task',
    'task_status_updated': 'updated task status',
    'task_comment_added': 'commented on a task',
    'task_attachment_added': 'added an attachment to a task',
    'work_readiness_activity_created': 'added a new Work Readiness activity',
    'work_readiness_submission_message': 'replied on a Work Readiness submission',
    'work_readiness_submission_created': 'submitted a Work Readiness activity',
    'internship_clinical_placement_opportunity_created': 'created a new opportunity',
    'internship_clinical_placement_staff_assigned': 'assigned you to an opportunity',
    'internship_clinical_placement_enrolled': 'submitted an enrollment request',
    'internship_clinical_placement_resubmitted': 'resubmitted an enrollment request',
    'internship_clinical_placement_enrollment_approved': 'approved your enrollment request',
    'internship_clinical_placement_enrollment_rejected': 'rejected your enrollment request',
    'internship_clinical_placement_enrollment_message': 'sent a message on an enrollment',
    'volunteering_opportunity_published': 'published a new volunteering opportunity',
    'volunteering_application_submitted': 'submitted a volunteering application',
    'volunteering_application_approved': 'approved your volunteering application',
    'volunteering_application_rejected': 'rejected your volunteering application',
    'volunteering_application_returned': 'returned your volunteering application',
    'volunteering_application_resubmitted': 'resubmitted a volunteering application',
    'volunteering_application_withdrawn': 'withdrew a volunteering application',
    'volunteering_hours_submitted': 'submitted volunteering hours',
    'volunteering_hours_credited': 'credited volunteering hours',
    'volunteering_hours_rejected': 'rejected your volunteering hours',
    'volunteering_hours_returned': 'returned your volunteering hours'
};

const NOTIFICATION_EMOJIS = {
    'liked': '👍',
    'commented': '💬',
    'replied': '↩️',
    'thinktank_like': '💡',
    'thinktank_comment': '💭',
    'thinktank_reply': '↩️',
    'submitted': '📝',
    'comment_added': '💬',
    'attachment_added': '📎',
    'work_readiness_activity_created': '🎯',
    'work_readiness_submission_message': '💬',
    'work_readiness_submission_created': '📝',
    'internship_clinical_placement_opportunity_created': '📣',
    'internship_clinical_placement_staff_assigned': '✅',
    'internship_clinical_placement_enrolled': '📝',
    'internship_clinical_placement_resubmitted': '🔄',
    'internship_clinical_placement_enrollment_approved': '✅',
    'internship_clinical_placement_enrollment_rejected': '❌',
    'internship_clinical_placement_enrollment_message': '💬',
    'volunteering_opportunity_published': '📣',
    'volunteering_application_submitted': '📝',
    'volunteering_application_approved': '✅',
    'volunteering_application_rejected': '❌',
    'volunteering_application_returned': '↩️',
    'volunteering_application_resubmitted': '🔄',
    'volunteering_application_withdrawn': '🚪',
    'volunteering_hours_submitted': '⏱️',
    'volunteering_hours_credited': '🏆',
    'volunteering_hours_rejected': '❌',
    'volunteering_hours_returned': '↩️',
    'approved': '✅',
    'rejected': '❌',
    'resubmitted': '🔄',
    'created': '🎯'
};

// GraphQL Queries
const GET_NOTIFICATIONS_QUERY = gql`
    query GetNotifications($first: Int!, $page: Int!, $action_type: String, $read: Boolean) {
        notifications(first: $first, page: $page, action_type: $action_type, read: $read) {
            error
            error_code
            message
            data {
                id
                type
                title
                message
                data
                read_at
                created_at
                user {
                    id
                    name
                }
                actor {
                    id
                    name
                    profile_picture_url
                }
                target_type
                action_type
            }
            paginatorInfo {
                total
                count
                perPage
                currentPage
                lastPage
                hasMorePages
                firstItem
                lastItem
            }
        }
    }
`;

const GET_UNREAD_COUNT_QUERY = gql`
    query GetUnreadCount {
        unreadNotificationsCount {
            error
            error_code
            message
            data {
                count
            }
        }
    }
`;

const MARK_AS_READ_MUTATION = gql`
    mutation MarkAsRead($id: ID!) {
        markNotificationAsRead(id: $id) {
            error
            error_code
            message
            data {
                id
                type
                title
                message
                data
                read_at
                created_at
            }
        }
    }
`;

const MARK_AS_UNREAD_MUTATION = gql`
    mutation MarkAsUnread($id: ID!) {
        markNotificationAsUnread(id: $id) {
            error
            error_code
            message
            data {
                id
                type
                title
                message
                data
                read_at
                created_at
            }
        }
    }
`;

const MARK_ALL_AS_READ_MUTATION = gql`
    mutation MarkAllAsRead {
        markAllNotificationsAsRead {
            error
            error_code
            message
            data {
                count
            }
        }
    }
`;

const DELETE_NOTIFICATION_MUTATION = gql`
    mutation DeleteNotification($id: ID!) {
        deleteNotification(id: $id) {
            error
            error_code
            message
            data
        }
    }
`;

import gql from 'graphql-tag';
import Pagination from '../../components/shared/pagination.vue';
import { formatDateToServerFormat } from "../../helpers/utils";

export default {
    name: "notifications",
    components: {
        Pagination
    },
    data() {
        return {
            notifications: [],
            unreadCount: 0,
            currentPage: 1,
            perPage: 10,
            pagination: {
                currentPage: 1,
                lastPage: 1,
                perPage: 20,
                total: 0
            },
            filterType: '',
            filterRead: '',
            DEFAULT_AVATAR,
        };
    },
    apollo: {
        notifications() {
            return {
                query: GET_NOTIFICATIONS_QUERY,
                variables() {
                    const variables = {
                        first: this.perPage,
                        page: this.currentPage
                    };

                    if (this.filterType) {
                        variables.action_type = this.filterType;
                    }

                    if (this.filterRead !== '') {
                        variables.read = this.filterRead === 'true';
                    }

                    return variables;
                },
                fetchPolicy: 'cache-and-network',
                update: (data) => {
                    const response = data.notifications;
                    if (response && response.paginatorInfo) {
                        this.pagination = response.paginatorInfo;
                    }
                    return response && response.data ? response.data : [];
                },
                error(error) {
                    console.error('Error fetching notifications:', error);
                }
            }
        },
        unreadCount() {
            return {
                query: GET_UNREAD_COUNT_QUERY,
                fetchPolicy: 'cache-and-network',
                update: (data) => {
                    if (data && data.unreadNotificationsCount && data.unreadNotificationsCount.data) {
                        return data.unreadNotificationsCount.data.count || 0;
                    }
                    return 0;
                },
                error(error) {
                    console.error('Error fetching unread count:', error);
                }
            }
        }
    },
    computed: {
        hasNotifications() {
            return this.notifications && this.notifications.length > 0;
        },
        hasUnreadNotifications() {
            return this.unreadCount > 0;
        },
        readCount() {
            return this.pagination.total - this.unreadCount;
        }
    },
    methods: {
        changePage(page) {
            if (page >= 1 && page <= this.pagination.lastPage) {
                this.currentPage = page;
                this.$apollo.queries.notifications.refetch();
            }
        },

        refetchNotifications() {
            this.currentPage = 1; // Reset to first page when filtering
            this.$apollo.queries.notifications.refetch();
        },

        async markAsRead(notificationId) {
            try {
                const result = await this.$apollo.mutate({
                    mutation: MARK_AS_READ_MUTATION,
                    variables: { id: notificationId }
                });

                if (result.data && result.data.markNotificationAsRead && result.data.markNotificationAsRead.error) {
                    throw new Error(result.data.markNotificationAsRead.message);
                }

                // Update local state - use Vue's reactivity system properly
                const index = this.notifications.findIndex(n => n.id === notificationId);
                if (index !== -1) {
                    this.notifications = [
                        ...this.notifications.slice(0, index),
                        {
                            ...this.notifications[index],
                            read_at: formatDateToServerFormat(new Date())
                        },
                        ...this.notifications.slice(index + 1)
                    ];
                    this.unreadCount = Math.max(0, this.unreadCount - 1);
                }
            } catch (error) {
                console.error('Error marking notification as read:', error);
                this.handleError('Failed to mark notification as read');
            }
        },

        async markAsUnread(notificationId) {
            try {
                const result = await this.$apollo.mutate({
                    mutation: MARK_AS_UNREAD_MUTATION,
                    variables: { id: notificationId }
                });

                if (result.data && result.data.markNotificationAsUnread && result.data.markNotificationAsUnread.error) {
                    throw new Error(result.data.markNotificationAsUnread.message);
                }

                // Update local state - use Vue's reactivity system properly
                const index = this.notifications.findIndex(n => n.id === notificationId);
                if (index !== -1) {
                    this.notifications = [
                        ...this.notifications.slice(0, index),
                        {
                            ...this.notifications[index],
                            read_at: null
                        },
                        ...this.notifications.slice(index + 1)
                    ];
                    this.unreadCount += 1;
                }
            } catch (error) {
                console.error('Error marking notification as unread:', error);
                this.handleError('Failed to mark notification as unread');
            }
        },

        async markAllAsRead() {
            try {
                const result = await this.$apollo.mutate({
                    mutation: MARK_ALL_AS_READ_MUTATION
                });

                if (result.data && result.data.markAllNotificationsAsRead && result.data.markAllNotificationsAsRead.error) {
                    throw new Error(result.data.markAllNotificationsAsRead.message);
                }

                // Update local state - use Vue's reactivity system properly
                this.notifications = this.notifications.map(notification => ({
                    ...notification,
                    read_at: formatDateToServerFormat(new Date())
                }));
                this.unreadCount = 0;

                // Force Vue to re-render the component
                this.$forceUpdate();
            } catch (error) {
                console.error('Error marking all notifications as read:', error);
                this.handleError('Failed to mark all notifications as read');
            }
        },

        async deleteNotification(notificationId) {
            try {
                const result = await this.$apollo.mutate({
                    mutation: DELETE_NOTIFICATION_MUTATION,
                    variables: { id: notificationId }
                });

                if (result.data && result.data.deleteNotification && result.data.deleteNotification.error) {
                    throw new Error(result.data.deleteNotification.message);
                }

                // Remove from local state
                const index = this.notifications.findIndex(n => n.id === notificationId);
                if (index > -1) {
                    const notification = this.notifications[index];
                    if (!notification.read_at) {
                        this.unreadCount = Math.max(0, this.unreadCount - 1);
                    }
                    this.notifications.splice(index, 1);
                    this.pagination.total -= 1;
                }
            } catch (error) {
                console.error('Error deleting notification:', error);
                this.handleError('Failed to delete notification');
            }
        },

        getNotificationViewLink(notification) {
            if (!notification || !notification.data) return null;
            const obj = this.parsedNotificationData(notification.data);
            if (!obj) return null;

            switch (notification.type) {
                case 'thinktank_like':
                case 'thinktank_comment':
                case 'thinktank_reply':
                    return { url: '/thinktank', label: 'View post' };
                case 'innovation_like':
                case 'innovation_comment':
                case 'innovation_reply':
                    return { url: '/innovation', label: 'View post' };
                case 'follow_started':
                case 'follow_requested':
                    if (obj.follower_token) {
                        return { url: `/profile/${obj.follower_token}`, label: 'View profile' };
                    }
                    return { url: '/my-profile', label: 'View profile' };
                case 'follow_request_accepted':
                    if (obj.acceptor_token) {
                        return { url: `/profile/${obj.acceptor_token}`, label: 'View profile' };
                    }
                    return { url: '/my-profile', label: 'View profile' };
                case 'profile_update':
                    return { url: '/my-profile', label: 'View profile' };
                case 'job_application':
                    if (obj.job_id) {
                        return { url: `/jobs/${obj.job_id}`, label: 'View job' };
                    }
                    return null;
                case 'event_registration':
                    if (obj.event_token) {
                        return { url: `/events/${obj.event_token}`, label: 'View event' };
                    }
                    return null;
                case 'survey_response':
                    if (obj.survey_slug) {
                        return { url: `/survey/${obj.survey_slug}/responses`, label: 'View survey' };
                    }
                    return { url: '/survey', label: 'View survey' };
                case 'task_board_created':
                case 'task_board_member_added':
                    if (obj.board_id) {
                        return { url: `/tasks/board/${obj.board_id}`, label: 'View board' };
                    }
                    return { url: '/tasks', label: 'View tasks' };
                case 'task_assigned':
                case 'task_status_updated':
                case 'task_completed':
                case 'task_comment_added':
                case 'task_attachment_added':
                    if (obj.task_id) {
                        return { url: `/tasks/task/${obj.task_id}`, label: 'View task' };
                    }
                    if (obj.board_id) {
                        return { url: `/tasks/board/${obj.board_id}`, label: 'View board' };
                    }
                    return { url: '/tasks', label: 'View tasks' };
                case 'work_readiness_activity_created':
                    if (obj.activity_token) {
                        return { url: `/my-work-readiness-program/${obj.activity_token}`, label: 'View activity' };
                    }
                    return { url: '/my-work-readiness-program', label: 'View Work Readiness Program' };
                case 'work_readiness_submission_message':
                    if (obj.activity_token) {
                        if (obj.is_student_sender) {
                            return { url: `/submitted-work-readiness-program/${obj.activity_token}`, label: 'View submission' };
                        }
                        return { url: `/my-work-readiness-program/${obj.activity_token}`, label: 'View activity' };
                    }
                    return { url: '/my-work-readiness-program', label: 'View Work Readiness Program' };
                case 'work_readiness_submission_created':
                    if (obj.activity_token) {
                        return { url: `/submitted-work-readiness-program/${obj.activity_token}`, label: 'View submissions' };
                    }
                    return { url: '/work-readiness-program', label: 'View Work Readiness Program' };
                case 'internship_clinical_placement_opportunity_created':
                case 'internship_clinical_placement_staff_assigned':
                case 'internship_clinical_placement_enrolled':
                case 'internship_clinical_placement_resubmitted':
                case 'internship_clinical_placement_enrollment_approved':
                case 'internship_clinical_placement_enrollment_rejected':
                case 'internship_clinical_placement_enrollment_message':
                    if (obj.opportunity_token) {
                        return {
                            url: '/internship-clinical-placement/' + obj.opportunity_token,
                            label: 'View opportunity',
                        };
                    }
                    return { url: '/internship-clinical-placement', label: 'View opportunities' };
                case 'volunteering_opportunity_published':
                case 'volunteering_application_submitted':
                case 'volunteering_application_approved':
                case 'volunteering_application_rejected':
                case 'volunteering_application_returned':
                case 'volunteering_application_resubmitted':
                case 'volunteering_application_withdrawn':
                case 'volunteering_hours_submitted':
                case 'volunteering_hours_credited':
                case 'volunteering_hours_rejected':
                case 'volunteering_hours_returned':
                    if (obj.opportunity_token) {
                        let volUrl = '/volunteering-and-rewards-hub/' + obj.opportunity_token;
                        if (obj.application_id) {
                            volUrl += '?application=' + obj.application_id;
                        } else if (obj.hour_submission_id) {
                            volUrl += '?hours=' + obj.hour_submission_id;
                        }
                        var volLabel = 'View opportunity';
                        if (obj.application_id) {
                            volLabel = 'View Application';
                        } else if (obj.hour_submission_id) {
                            volLabel = 'View Hours';
                        }
                        return { url: volUrl, label: volLabel };
                    }
                    return { url: '/volunteering-and-rewards-hub', label: 'View volunteering hub' };
                default:
                    if (obj.token) {
                        return { url: '/thinktank', label: 'View post' };
                    }
                    return null;
            }
        },

        navigateToNotification(notification) {
            const link = this.getNotificationViewLink(notification);
            if (link && link.url) {
                if (notification && !notification.read_at) {
                    this.markAsRead(notification.id);
                }
                const resolved = this.$router.resolve(link.url);
                const href = resolved && resolved.href ? resolved.href : link.url;
                window.open(href, '_blank');
            }
        },

        formatTime(timestamp) {
            try {
                const date = new Date(timestamp);
                const now = new Date();
                const diff = now - date;

                if (diff < TIME_INTERVALS.MINUTE) return 'Just now';
                if (diff < TIME_INTERVALS.HOUR) return `${Math.floor(diff / TIME_INTERVALS.MINUTE)}m ago`;
                if (diff < TIME_INTERVALS.DAY) return `${Math.floor(diff / TIME_INTERVALS.HOUR)}h ago`;
                if (diff < TIME_INTERVALS.MONTH) return `${Math.floor(diff / TIME_INTERVALS.DAY)}d ago`;

                return date.toLocaleDateString();
            } catch (error) {
                console.warn('Error formatting time:', error);
                return 'Unknown time';
            }
        },

        getProfilePicture(actor) {
            if (actor && actor.profile_picture_url) {
                return process.env.VUE_APP_PUBLIC_URL + actor.profile_picture_url;
            }
            return this.DEFAULT_AVATAR;
        },

        getUserName(actor) {
            return actor && actor.name ? actor.name : 'User';
        },

        handleImageError(event) {
            event.target.src = this.DEFAULT_AVATAR;
        },

        getNotificationEmoji(type) {
            return NOTIFICATION_EMOJIS[type] || '🔔';
        },

        getFormattedNotificationText(item) {
            const actorName = this.getUserName(item.actor);
            const actionText = this.getActionText(item.type);
            const content = this.getContentText(item.data, item.type);

            return `${actorName} ${actionText}${content}`;
        },

        getActionText(actionType) {
            return NOTIFICATION_ACTIONS[actionType] || 'sent you a notification';
        },

        getContentText(data, notificationType) {
            try {
                const obj = this.parsedNotificationData(data);

                // For task board notifications, use board_name
                if (notificationType === 'task_board_created' || notificationType === 'task_board_member_added') {
                    if (obj && obj.board_name) {
                        return ` "${obj.board_name}"`;
                    }
                }

                // For task notifications, use task_title
                if (notificationType === 'task_assigned' || notificationType === 'task_status_updated' || notificationType === 'task_comment_added' || notificationType === 'task_attachment_added') {
                    if (obj && obj.task_title) {
                        return ` "${obj.task_title}"`;
                    }
                }

                // For work readiness activity created, use activity_title
                if (notificationType === 'work_readiness_activity_created') {
                    if (obj && obj.activity_title) {
                        return ` "${obj.activity_title}"`;
                    }
                }

                if (notificationType === 'work_readiness_submission_message') {
                    if (obj && obj.message_excerpt) {
                        return `: "${obj.message_excerpt}"`;
                    }
                    if (obj && obj.activity_title) {
                        return ` on "${obj.activity_title}"`;
                    }
                }

                if (notificationType === 'work_readiness_submission_created') {
                    if (obj && obj.activity_title) {
                        return ` "${obj.activity_title}"`;
                    }
                }

                if (
                    notificationType === 'internship_clinical_placement_opportunity_created'
                    || notificationType === 'internship_clinical_placement_staff_assigned'
                    || notificationType === 'internship_clinical_placement_enrolled'
                    || notificationType === 'internship_clinical_placement_resubmitted'
                    || notificationType === 'internship_clinical_placement_enrollment_approved'
                    || notificationType === 'internship_clinical_placement_enrollment_rejected'
                    || notificationType === 'internship_clinical_placement_enrollment_message'
                    || notificationType === 'volunteering_opportunity_published'
                    || notificationType === 'volunteering_application_submitted'
                    || notificationType === 'volunteering_application_approved'
                    || notificationType === 'volunteering_application_rejected'
                    || notificationType === 'volunteering_application_returned'
                    || notificationType === 'volunteering_application_resubmitted'
                    || notificationType === 'volunteering_application_withdrawn'
                    || notificationType === 'volunteering_hours_submitted'
                    || notificationType === 'volunteering_hours_credited'
                    || notificationType === 'volunteering_hours_rejected'
                    || notificationType === 'volunteering_hours_returned'
                ) {
                    if (obj && obj.message_excerpt) {
                        return `: "${obj.message_excerpt}"`;
                    }
                    if (obj && obj.reason_excerpt) {
                        return `: "${obj.reason_excerpt}"`;
                    }
                    if (obj && obj.opportunity_title) {
                        return ` "${obj.opportunity_title}"`;
                    }
                }

                if (obj && obj.content) {
                    return `: "${obj.content}"`;
                }

                if (obj && obj.comment_excerpt) {
                    return `: "${obj.comment_excerpt}"`;
                }

                if (obj && obj.reply_excerpt) {
                    return `: "${obj.reply_excerpt}"`;
                }

                if (obj && obj.post_title) {
                    return `: "${obj.post_title}"`;
                }

                return '';
            } catch (error) {
                console.warn('Error parsing notification content:', error);
                return '';
            }
        },

        parsedNotificationData(data) {
            try {
                if (data == null) {
                    return null;
                }
                if (typeof data === 'object') {
                    return data;
                }
                if (typeof data === 'string') {
                    return JSON.parse(data);
                }
                return null;
            } catch (error) {
                console.warn('Error parsing notification data:', error);
                return null;
            }
        },

        handleError(message) {
            // You can add toast notification here if available
            console.error(message);
            // this.$toast.error(message);
        }
    },
}
</script>

<style scoped>
.notification-item {
    transition: all 0.2s ease-in-out;
}

.notification-item:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.profile-picture {
    transition: transform 0.2s ease-in-out;
}

.emoji-badge {
    transition: all 0.2s ease-in-out;
}

/* Custom scrollbar for better UX */
::-webkit-scrollbar {
    width: 6px;
}

::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
}

::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
}
</style>